import React, { useCallback, useMemo, useState } from 'react';

import { ACTION_POSITIONS, MODAL_TYPE, ModalTypeOptions } from './Config';
import ModalPopup from './ModalPopup/ModalPopup';

export type useConfirmPopupReturn = [React.FC, () => Promise<boolean>, (event?: any) => void];

type CustomButton = {
  label: string;
  disabled?: boolean;
};

export type UseConfirmPopupProps = {
  title: React.ReactNode;
  icon?: { icon: string; iconType?: string; className?: string };
  message?: string;
  confirmBtn?: boolean | CustomButton;
  cancelBtn?: boolean | CustomButton;
  modalId?: string;
  classNames?: string;
  modalWidth?: string;
  modalType?: ModalTypeOptions;
  actionPosition?: (typeof ACTION_POSITIONS)[keyof typeof ACTION_POSITIONS];
};

const useConfirmPopup = ({
  title,
  icon = null,
  message = 'Do you wish to proceed?',
  modalId = 'confirm-modal',
  confirmBtn = null,
  cancelBtn = null,
  classNames = '',
  modalWidth = '520px',
  modalType = MODAL_TYPE.default,
  actionPosition = ACTION_POSITIONS.between,
}: UseConfirmPopupProps): useConfirmPopupReturn => {
  const [promise, setPromise] = useState(null);
  const body = useMemo(
    () => ({
      title,
      icon,
      message,
    }),
    [title, icon, message]
  );

  const optionalProps = useMemo(
    () => ({
      ...(confirmBtn !== true && { confirmBtn }),
      ...(cancelBtn !== true && { cancelBtn }),
    }),
    [confirmBtn, cancelBtn]
  );

  const confirm = useCallback(
    (): Promise<boolean> =>
      new Promise((resolve) => {
        setPromise({ resolve });
      }),
    []
  );

  const handleClose = useCallback((event) => {
    event.stopPropagation();

    setPromise(null);
  }, []);

  const handleConfirm = useCallback(
    (event) => {
      event.stopPropagation();

      promise.resolve(true);
      handleClose(event);
    },
    [promise, handleClose]
  );

  const handleCancel = useCallback(
    (event) => {
      event.stopPropagation();

      promise.resolve(false);
      handleClose(event);
    },
    [promise, handleClose]
  );

  const ConfirmationModal = useCallback(
    () => (
      <ModalPopup
        id={modalId}
        open={promise !== null}
        title={body.title}
        icon={body.icon}
        message={body.message}
        className={classNames}
        modalWidth={modalWidth}
        modalType={modalType}
        actionPosition={actionPosition}
        {...optionalProps}
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    ),
    [
      promise,
      modalId,
      body.title,
      body.icon,
      body.message,
      classNames,
      handleCancel,
      handleConfirm,
      modalWidth,
      modalType,
      actionPosition,
      optionalProps,
    ]
  );

  return [ConfirmationModal, confirm, handleClose];
};

export default useConfirmPopup;
